import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { CompetitorMap } from '../components/dashboard/CompetitorMap';
import { FloatingFAB } from '../components/dashboard/FloatingFAB';
import { PeriodFilter } from '../components/dashboard/PeriodFilter';
import { getRegionColor, fmtM } from '../components/dashboard/theme';
import mapData from '../components/dashboard/map_data.json';

const API = process.env.REACT_APP_BACKEND_URL || '';

const round1 = (v) => Math.round(v * 10) / 10;

// Rebuilds the same {sharat_dest, suppliers, n_exporters} shape as
// competitor_explorer_full.json, but aggregated over an arbitrary month
// range from the monthly-dimensioned competitor_explorer.json rows.
function buildFilteredData(monthly, fromIdx, toIdx) {
  const inRange = (mi) => mi >= fromIdx && mi <= toIdx;
  const SHARAT_NAME = monthly.sharat;

  const expMap = new Map();
  for (const r of monthly.exporters) {
    if (!inRange(r.mi)) continue;
    const cur = expMap.get(r.e) || { val: 0, qty: 0, ship: 0 };
    cur.val += r.v; cur.qty += r.q; cur.ship += r.s;
    expMap.set(r.e, cur);
  }

  const destMap = new Map();
  for (const r of monthly.exp_countries) {
    if (!inRange(r.mi)) continue;
    let cm = destMap.get(r.e);
    if (!cm) { cm = new Map(); destMap.set(r.e, cm); }
    const cur = cm.get(r.c) || { v: 0, q: 0 };
    cur.v += r.v; cur.q += r.q;
    cm.set(r.c, cur);
  }

  const portMap = new Map();
  for (const r of monthly.exp_ports) {
    if (!inRange(r.mi)) continue;
    let pm = portMap.get(r.e);
    if (!pm) { pm = new Map(); portMap.set(r.e, pm); }
    const cur = pm.get(r.p) || { v: 0, q: 0 };
    cur.v += r.v; cur.q += r.q;
    pm.set(r.p, cur);
  }

  const formMap = new Map();
  for (const r of monthly.exp_forms) {
    if (!inRange(r.mi)) continue;
    let fm = formMap.get(r.e);
    if (!fm) { fm = new Map(); formMap.set(r.e, fm); }
    const cur = fm.get(r.f) || { v: 0, q: 0 };
    cur.v += r.v; cur.q += r.q;
    fm.set(r.f, cur);
  }

  const sharatDest = new Set();
  const sharatCm = destMap.get(SHARAT_NAME);
  if (sharatCm) {
    for (const [c, v] of sharatCm) if (v.v > 0) sharatDest.add(c);
  }

  const suppliers = [];
  for (const [name, tot] of expMap) {
    if (tot.val <= 0) continue;

    const cm = destMap.get(name);
    const dest = cm
      ? Array.from(cm.entries())
          .filter(([, v]) => v.v > 0)
          .map(([c, v]) => ({ c, v: round1(v.v), q: round1(v.q / 1000) }))
          .sort((a, b) => b.v - a.v)
      : [];

    const pm = portMap.get(name);
    const ports = pm
      ? Array.from(pm.entries())
          .filter(([, v]) => v.v > 0)
          .map(([p, v]) => ({ n: p, c: '', v: round1(v.v) }))
          .sort((a, b) => b.v - a.v)
          .slice(0, 8) // matches the top-8 cap baked into competitor_explorer_full.json
      : [];

    const fm = formMap.get(name);
    const forms = fm
      ? Array.from(fm.entries())
          .filter(([, v]) => v.v > 0)
          .map(([f, v]) => ({ name: f, val: round1(v.v) }))
          .sort((a, b) => b.val - a.val)
      : [];

    suppliers.push({
      name,
      val: round1(tot.val),
      qty: round1(tot.qty / 1000),
      ship: tot.ship,
      n_dest: dest.length,
      is_sharat: name === SHARAT_NAME,
      dest,
      ports,
      forms,
    });
  }
  suppliers.sort((a, b) => b.val - a.val);

  return {
    sharat_dest: Array.from(sharatDest),
    suppliers,
    n_exporters: suppliers.length,
  };
}

const GUIDE = {
  sections: [
    { title: 'Exporters List', desc: 'All Indian shrimp exporters ranked by export value. Sharat is highlighted with a star. Click any exporter to see where they ship on the world map.', example: "Example: Devi Fisheries ($419M, 23 countries) ships to far more markets than Sharat ($64M, 12 countries) — showing Sharat's geographic gap." },
    { title: 'World Map Bubbles', desc: 'Each bubble is a destination country the selected exporter ships to, sized by export value. White-ringed bubbles = countries where Sharat also ships (overlap).', example: 'Example: Click Devi Fisheries — if Belgium has a white ring, both Devi and Sharat compete there. No ring on Germany = Devi ships there but Sharat doesn’t.' },
    { title: 'Overlap Count', desc: 'The info panel shows how many countries the selected exporter shares with Sharat. Higher overlap = direct competitor. Zero overlap = different market strategy.', example: 'Example: If NAS Fisheries shares 8 of 12 Sharat markets, they’re a direct rival. A firm sharing 0 markets operates in a completely different space.' },
    { title: 'Search & Filter', desc: 'Use the search box to find specific exporters. Scroll the list to compare sizes — the value shown is proportional to export value.', example: 'Example: Search "Castlerock" to instantly compare their destinations against Sharat’s footprint.' },
  ],
  lookFor: "Competitors shipping to Sharat's markets at higher volume = defend those accounts. Competitors in markets Sharat doesn't serve = potential expansion targets worth studying.",
};

/* ── Skeleton ── */
const SB = ({ className = '', style, delay = 0 }) => (
  <div className={`skel-bone skel-pill ${className}`} style={{ ...style, animationDelay: `${delay}ms` }} />
);

const ExplorerSkeleton = () => (
  <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="explorer-skeleton">
    <div className="max-w-[1340px] mx-auto">
      <SB className="h-2 w-64 mb-2" delay={0} />
      <SB className="h-6 w-[480px] mb-1.5" delay={30} />
      <SB className="h-3 w-[560px] mb-4" delay={60} />
      <div className="grid grid-cols-[320px_1fr] gap-3.5">
        <div className="rounded-2xl p-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
          <SB className="h-9 w-full mb-2.5 !rounded-lg" delay={100} />
          {Array.from({ length: 10 }, (_, i) => (
            <SB key={i} className="h-8 w-full mb-1.5" delay={120 + i * 25} />
          ))}
        </div>
        <div className="rounded-2xl overflow-hidden relative h-[400px]" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
          {[
            { x: '22%', y: '28%', s: 44 }, { x: '50%', y: '22%', s: 28 },
            { x: '76%', y: '34%', s: 36 }, { x: '60%', y: '44%', s: 18 },
            { x: '30%', y: '54%', s: 14 }, { x: '84%', y: '58%', s: 22 },
          ].map((b, i) => (
            <div key={i} className="skel-bone skel-round absolute" style={{ left: b.x, top: b.y, width: b.s, height: b.s, animationDelay: `${240 + i * 50}ms` }} />
          ))}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4 mt-3.5">
        {[0, 1, 2].map(c => (
          <div key={c} className="rounded-xl p-4" style={{ background: 'var(--skel-card)', border: '1px solid var(--skel-border)' }}>
            <SB className="h-3 w-28 mb-3" delay={500 + c * 40} />
            {[0, 1, 2, 3, 4].map(i => (
              <SB key={i} className="h-3 w-full mb-2" delay={520 + c * 40 + i * 20} />
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ── Exporter list row ── */
const ExporterRow = ({ s, isSelected, onClick }) => (
  <div
    onClick={onClick}
    className={`grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5 px-2.5 py-2 rounded-lg cursor-pointer border transition ${
      isSelected
        ? (s.is_sharat ? 'bg-[rgba(255,107,82,0.2)] border-[#C9543F]' : 'bg-[rgba(79,195,201,0.13)] border-dash-ice')
        : (s.is_sharat ? 'bg-[rgba(255,107,82,0.1)] border-[#C9543F]' : 'border-transparent hover:bg-white/[0.04]')
    }`}
    data-testid={`exporter-row-${s.name}`}
  >
    <span className={`text-[12.5px] truncate ${s.is_sharat ? 'text-dash-coral font-bold' : 'text-dash-ink'}`}>
      {s.is_sharat ? '★ ' : ''}{s.name}
    </span>
    <span className="font-mono text-[11px] text-dash-amber text-right">{fmtM(s.val)}</span>
    <span className="col-start-1 font-mono text-[9.5px] text-[#5C7980]">{s.n_dest} countries · {s.qty} t</span>
  </div>
);

/* ── Ranked bar list (Destinations / Ports / Product mix columns) ── */
const RankBars = ({ items, colorOf, tagOf }) => {
  if (!items || !items.length) return <div className="text-dash-mut text-xs">—</div>;
  const maxVal = Math.max(...items.map(it => it.val), 1);
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((it, i) => (
        <div key={it.name + i} className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-0.5 text-xs">
          <span className="truncate text-dash-ink" title={it.name}>
            {it.name}
            {it.rg && <span className="text-[9px] text-[#5C7980] font-mono ml-1.5">{it.rg}</span>}
            {tagOf?.(it) && <span className="text-[8.5px] text-dash-coral border border-[#C9543F] rounded px-1 ml-1.5 font-mono">SHARAT MKT</span>}
          </span>
          <span className="font-mono text-[11px] text-dash-mut text-right">{fmtM(it.val)}</span>
          <div className="col-span-2 h-1.5 bg-dash-panel2 rounded overflow-hidden">
            <div className="h-full rounded" style={{ width: `${(it.val / maxVal * 100).toFixed(0)}%`, background: colorOf ? colorOf(it) : 'linear-gradient(90deg,#7FE3E0,#4FC3C9)' }} />
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── Main Page ── */
export default function CompetitorExplorerReact() {
  const [origData, setOrigData] = useState(null);
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [months, setMonths] = useState([]);
  const [fromIdx, setFromIdx] = useState(0);
  const [toIdx, setToIdx] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedName, setSelectedName] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/static/competitor_explorer_full.json`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
      fetch(`${API}/api/static/competitor_explorer.json`, { credentials: 'include' }).then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); }),
    ]).then(([full, mon]) => {
      setOrigData(full);
      setData(full);
      setMonthly(mon);
      setMonths(mon.months || []);
      setToIdx((mon.months?.length || 1) - 1);
      const sh = full.suppliers.find(s => s.is_sharat);
      setSelectedName(sh?.name || full.suppliers[0]?.name || null);
      setLoading(false);
    }).catch(e => { setError(e.message); setLoading(false); });
  }, []);

  const applyPeriod = useCallback(() => {
    if (!monthly) return;
    if (fromIdx > toIdx) { alert('From must be before To'); return; }
    setData(buildFilteredData(monthly, fromIdx, toIdx));
    setIsFiltered(true);
  }, [monthly, fromIdx, toIdx]);

  const resetPeriod = useCallback(() => {
    if (!origData) return;
    setData(origData);
    setIsFiltered(false);
  }, [origData]);

  const sharatSet = useMemo(() => new Set(data?.sharat_dest || []), [data]);

  const { sharatRow, restRows, shownCount } = useMemo(() => {
    if (!data) return { sharatRow: null, restRows: [], shownCount: 0 };
    const term = searchTerm.trim().toLowerCase();
    const sharat = data.suppliers.find(s => s.is_sharat);
    const rest = data.suppliers.filter(s => !s.is_sharat && s.name.toLowerCase().includes(term));
    const showSharat = !term || sharat?.name.toLowerCase().includes(term);
    return {
      sharatRow: showSharat ? sharat : null,
      restRows: rest,
      shownCount: rest.length + (showSharat ? 1 : 0),
    };
  }, [data, searchTerm]);

  const handleSelect = useCallback((name) => {
    setSelectedName(name);
  }, []);

  if (loading) return <ExplorerSkeleton />;
  if (error) return <div className="h-full bg-dash-bg flex items-center justify-center"><div className="text-dash-coral font-mono text-sm">Error: {error}</div></div>;

  // Preserve the selection by name across period changes; fall back to
  // Sharat (or the first row) if the previously selected exporter had no
  // activity in the newly filtered range.
  const selected = data.suppliers.find(s => s.name === selectedName)
    || data.suppliers.find(s => s.is_sharat)
    || data.suppliers[0];

  const periodLabel = isFiltered
    ? `${months[fromIdx]} to ${months[toIdx]} (${toIdx - fromIdx + 1} month${toIdx - fromIdx === 0 ? '' : 's'})`
    : `All ${months.length || 23} months · ${months[0]} to ${months[months.length - 1]}`;

  const destItems = (selected.dest || []).map(d => ({
    name: d.c,
    val: d.v,
    rg: mapData.coords[d.c]?.region,
    _sharat: sharatSet.has(d.c),
  })).sort((a, b) => b.val - a.val);
  const portItems = (selected.ports || []).map(p => ({ name: p.n, val: p.v, rg: p.c })).sort((a, b) => b.val - a.val);
  const formItems = (selected.forms || []).slice().sort((a, b) => b.val - a.val);

  return (
    <div className="bg-dash-bg text-dash-ink font-grotesk p-5" data-testid="competitor-explorer-react">
      <div className="max-w-[1340px] mx-auto">

        {/* Header */}
        <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-dash-ice mb-2">
          Competitor Destination Explorer &middot; Apr 2024 – Feb 2026
        </div>
        <h1 className="font-grotesk font-extrabold text-[26px] tracking-tight mb-1">
          Click any exporter — see <span className="text-dash-coral">where their shrimp goes</span>
        </h1>
        <p className="text-dash-mut text-[13px] mb-4 max-w-[840px]">
          Pick Sharat or any competitor from the list. The world map lights up every country they ship to (bubble size = their value there). A <b className="text-dash-ink">white ring</b> marks countries where Sharat also sells — so you can spot head-to-head overlap instantly.
        </p>

        {/* Period Filter */}
        {months.length > 0 && (
          <div className="mb-4">
            <PeriodFilter
              months={months}
              fromIdx={fromIdx}
              toIdx={toIdx}
              onFromChange={setFromIdx}
              onToChange={setToIdx}
              onApply={applyPeriod}
              onReset={resetPeriod}
              periodLabel={periodLabel}
              isFiltered={isFiltered}
            />
          </div>
        )}

        {/* Main grid: exporter list + map */}
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-3.5 items-stretch">
          <div className="bg-dash-panel border border-dash-line rounded-2xl p-4 h-[640px] flex flex-col">
            <div className="font-grotesk font-bold text-sm mb-2">
              Exporters <span className="text-dash-mut font-normal text-xs">({shownCount} of {data.suppliers.length})</span>
            </div>
            <input
              type="text"
              placeholder="Search exporter…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full bg-dash-panel2 border border-dash-line rounded-lg px-2.5 py-2 text-[13px] text-dash-ink placeholder:text-[#5C7980] focus:outline-none focus:border-dash-ice mb-2"
              data-testid="exporter-search"
            />
            <div className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto pr-1" data-testid="exporter-list">
              {sharatRow && (
                <ExporterRow s={sharatRow} isSelected={selected.name === sharatRow.name} onClick={() => handleSelect(sharatRow.name)} />
              )}
              {sharatRow && restRows.length > 0 && (
                <div className="font-mono text-[9.5px] uppercase tracking-wider text-[#5C7980] px-2.5 pt-1.5 pb-0.5">Competitors</div>
              )}
              {restRows.map(s => (
                <ExporterRow key={s.name} s={s} isSelected={selected.name === s.name} onClick={() => handleSelect(s.name)} />
              ))}
              {shownCount === 0 && <div className="text-dash-mut text-xs text-center py-6">No exporters found</div>}
            </div>
          </div>

          <div className="bg-dash-panel border border-dash-line rounded-2xl p-4 h-[640px]">
            <CompetitorMap selected={selected} sharatSet={sharatSet} fillHeight />
          </div>
        </div>

        {/* Detail columns */}
        <div className="bg-dash-panel border border-dash-line rounded-2xl p-5 mt-3.5" data-testid="detail-panel">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_1fr] gap-5">
            <div>
              <h4 className="font-mono text-[10px] tracking-[0.08em] uppercase text-dash-ice mb-2.5">Destinations ({selected.n_dest})</h4>
              <RankBars items={destItems} colorOf={it => getRegionColor(it.name)} tagOf={it => it._sharat} />
            </div>
            <div>
              <h4 className="font-mono text-[10px] tracking-[0.08em] uppercase text-dash-ice mb-2.5">Top destination ports</h4>
              <RankBars items={portItems} colorOf={() => 'linear-gradient(90deg,#F2C46C,#F2B14C)'} />
            </div>
            <div>
              <h4 className="font-mono text-[10px] tracking-[0.08em] uppercase text-dash-ice mb-2.5">Product mix</h4>
              <RankBars items={formItems} colorOf={() => 'linear-gradient(90deg,#B98CD6,#8a5fb0)'} />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 border-t border-dash-line pt-4 text-[#5C7980] text-[11.5px] leading-relaxed">
          <b>How to use:</b> click any exporter (search to find one of {data.n_exporters}); the map shows every destination country they shipped to in Apr 2024 – Feb 2026, sized by value. White-ringed bubbles = countries where Sharat also sells (head-to-head overlap). Map aggregates to destination <b>country</b>; the ports column lists their actual arrival ports. Single-month snapshot.
        </div>

        <FloatingFAB guideContent={GUIDE} />
      </div>
    </div>
  );
}
